import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";
import { formatDateToUtc } from "../../../utils/formatDate";
import { useSelector } from "react-redux";
import LabelSelector from "./LabelSelector";
import SmallMultiple from "./SmallMultiple";

export const VolumeOverTime = (props) => {
  const [conceptData, setConceptData] = useState({});
  const [loading, setLoading] = useState(false);
  const { smoothLine } = props;
  const [getVolume] = useGetConceptsFiltersMutation();
  const [conceptColors, setConceptColors] = useState({});

  // State for common x-axis labels and selection.
  const [allLabels, setAllLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);

  const labelFontSize = useSelector((state) => {
    const fontSizes = state.fontManager.fontSizes;
    const normalizedKey = Object.keys(fontSizes).find(
      (key) => key.trim() === "labelFontSize"
    );
    return fontSizes[normalizedKey] || 16;
  });

  async function getVolumeData() {
    setLoading(true);
    const conceptFilter = getConceptFilter(props);
    const { data: dataConcepts } = await getVolume({
      id: props.id,
      concept_selector: conceptFilter,
      filter: props.filters,
      breakdowns: [{ name: props.selectedDateName, interval: props.interval }],
    });
    props.setFilterDocCount(dataConcepts.filter_count);
    if (props.conceptList) {
      props.setLocalConceptOptions(dataConcepts.match_counts);
    }

    const breakdownData = dataConcepts.breakdowns[0].buckets;
    const conceptsMap = {};
    const conceptColorsMap = {};

    breakdownData.forEach(({ label, match_counts, filter_count }) => {
      if (!filter_count) return;
      const formattedDate = formatDateToUtc(label, props.interval);
      match_counts.forEach(({ name, match_count, exact_match_count, color }) => {
        if (!conceptsMap[name]) {
          conceptsMap[name] = [];
          if (color) {
            conceptColorsMap[name] = color;
          }
        }
        let count = match_count;
        switch (props.seletctedType) {
          case "match_count":
            count = match_count;
            break;
          case "exact_match_count":
            count = exact_match_count;
            break;
          case "conceptual_match_count":
            count = match_count - exact_match_count;
            break;
          default:
            count = match_count;
            break;
        }
        conceptsMap[name].push({
          date: formattedDate,
          [name]: count,
        });
      });
    });

    setConceptData(conceptsMap);
    setConceptColors(conceptColorsMap);

    // Extract unique x-axis labels.
    const labelSet = new Set();
    Object.values(conceptsMap).forEach((dataPoints) => {
      dataPoints.forEach((dp) => {
        if (dp.date) labelSet.add(dp.date);
      });
    });
    const labels = Array.from(labelSet);
    // Assuming label format like "Q1 2023", sort by year and quarter.
    labels.sort((a, b) => {
      const yearA = a.match(/\d{4}/) ? parseInt(a.match(/\d{4}/)[0], 10) : 0;
      const yearB = b.match(/\d{4}/) ? parseInt(b.match(/\d{4}/)[0], 10) : 0;
      if (yearA !== yearB) return yearA - yearB;
      const quarterA = a.match(/Q(\d)/) ? parseInt(a.match(/Q(\d)/)[1], 10) : 0;
      const quarterB = b.match(/Q(\d)/) ? parseInt(b.match(/Q(\d)/)[1], 10) : 0;
      return quarterA - quarterB;
    });
    setAllLabels(labels);
    setSelectedLabels(labels); // Initially select all labels.
    
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    const debouncedGetVolume = debounce(getVolumeData, 1000);
    debouncedGetVolume();
    return () => {
      debouncedGetVolume.cancel();
    };
  }, [
    props.selectedPageSize,
    props.filters,
    props.conceptTypeDay,
    props.selectedDateName,
    props.interval,
    props.selectedLocalConcepts.length,
    props.conceptList,
    props.seletctedType,
  ]);

  // Filter aggregated data based on selectedLabels (dates).
  const filteredChartData = {};
  Object.entries(conceptData).forEach(([conceptName, dataPoints]) => {
    filteredChartData[conceptName] = dataPoints.filter((dp) =>
      selectedLabels.includes(dp.date)
    );
  });

  let labelText;
  switch (props.seletctedType) {
    case "match_count":
      labelText = "Total Matches";
      break;
    case "exact_match_count":
      labelText = "Exact Matches";
      break;
    case "conceptual_match_count":
      labelText = "Conceptual Matches";
      break;
    default:
      labelText = "Total Matches";
      break;
  }

  // Toggle a label on/off.
  const handleToggleLabel = (label) => {
    setSelectedLabels((prev) =>
      prev.includes(label)
        ? prev.filter((l) => l !== label)
        : [...prev, label]
    );
  };

  if (loading) {
    return (
      <Spin className="spinner-by-center" indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
    );
  }

  // Compute a global maximum Y value across all charts.
  let globalMax = 0;
  Object.entries(filteredChartData).forEach(([conceptName, dataPoints]) => {
    dataPoints.forEach((dp) => {
      const val = dp[conceptName] || 0;
      if (val > globalMax) globalMax = val;
    });
  });

  return (
    <div
      className="volume-compare"
      style={{ height: props.widget.layout.h * 39 - 150, overflow: "auto", display: "flex", flexDirection: "column" }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {/* Small multiples grid */}
      <div style={{ flex: "1 1 auto" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", padding: "0.5rem" }}>
          {Object.keys(filteredChartData).map((conceptName, idx) => {
            const data = filteredChartData[conceptName];
            const metricKey = props.visualizeType ? conceptName : `${conceptName} %`;
            // Define a color palette (or use conceptColors if available)
            const colorPalette = [
              "#A5D8FF",
              "#FFC9A8",
              "#FDFFA1",
              "#D2FF99",
              "#E6CCFF",
              "#FFB0C9",
              "#99FFEE",
              "#D5FF99",
              "#FFF59B",
              "#C4C4C4",
            ];
            const color = conceptColors[conceptName] || colorPalette[idx % colorPalette.length];
            return (
              <div
                key={conceptName}
                style={{
                  flex: "0 0 356px",
                  height: "220px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <SmallMultiple
                  data={data}
                  metric={metricKey}
                  color={color}
                  title={conceptName}
                  smoothLine={smoothLine}
                  maxYAxis={globalMax}
                />
              </div>
            );
          })}
        </div>
      </div>
      {/* Common clickable label selector */}
      <div style={{ flex: "0 0 auto", padding: "0.5rem 1rem" }}>
        {allLabels.length > 0 && (
          <LabelSelector
            labels={allLabels}
            selectedLabels={selectedLabels}
            onToggleLabel={handleToggleLabel}
          />
        )}
      </div>
    </div>
  );
};

export default VolumeOverTime;
